import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { agnosticAddMonths } from "@formant/date-fns/agnosticAddMonths";
import { getChargebeeBillingPeriodFor } from "@formant/date-fns/billingUsageDateFormat";
import { IOrganization } from "@formant/core/model/IOrganization";
import { IStripeInvoice } from "@formant/core/model/IStripeInvoice";
import { IUsageCost } from "@formant/core/model/IUsageCost";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import { countClient, usageMetricsClient } from "../../../clients";
import { authStore } from "../../../stores";
import { StandardButton } from "../../common/StandardButton";
import { Container } from "../../Container";
import { BillingUsage } from "../BillingUsage";
import styles from "./index.scss";
import { UsageCount } from "./UsageCount";

export interface IDataUsageProps {
    organization?: IOrganization;
    enableCost?: boolean;
    invoices?: IStripeInvoice[];
    selectedInvoice?: IStripeInvoice;
    onSelectInvoice?: (selectedInvoice: IStripeInvoice) => void;
}

@observer
export class DataUsage extends Component<IDataUsageProps> {
    @observable
    private startDate = new Date();

    @observable
    private usageData: IUsageCost[] | undefined;

    @observable
    // generating data usage across all organizations is expensive,
    // disable by default
    private populate = this.props.organization !== undefined;

    @observable
    private activeDeviceCount: number | undefined;

    async componentDidMount() {
        await this.loadMonthlyActiveDeviceCount();
    }

    async loadMonthlyActiveDeviceCount() {
        const token = await authStore.token();
        if (this.props.organization?.id) {
            this.usageData = await usageMetricsClient.getUsageCosts(
                token,
                this.props.organization?.id,
            );
        }

        this.activeDeviceCount = undefined;
        const startOfMonth = new Date(
            this.startDate.getFullYear(),
            this.startDate.getMonth(),
            1,
        );
        const endOfMonth = agnosticAddMonths(startOfMonth, 1);

        const activeDevices = await countClient.activeDevices(token, {
            start: startOfMonth.toISOString(),
            end: endOfMonth.toISOString(),
            organizationId: this.props.organization?.id,
        });

        if (activeDevices) {
            this.activeDeviceCount = activeDevices.length;
        }
    }

    public render() {
        const { organization } = this.props;

        const [start, end] = getChargebeeBillingPeriodFor(this.startDate);

        return this.populate ? (
            <>
                {organization && this.usageData && (
                    <BillingUsage
                        start={start}
                        end={end}
                        onNext={this.onNext}
                        onPrevious={this.onPrevious}
                        organization={organization}
                        usageCost={this.usageData}
                    />
                )}
                <Container className={styles.container}>
                    {this.activeDeviceCount !== undefined ? (
                        <div>
                            <UsageCount
                                title="Total Active Devices for current month"
                                count={this.activeDeviceCount}
                                cost={undefined}
                            />
                        </div>
                    ) : (
                        <LoadingIndicator visible={true} />
                    )}
                </Container>
            </>
        ) : (
            <StandardButton onClick={this.onLoad}>Load</StandardButton>
        );
    }

    private onLoad = () => {
        this.populate = true;
    };

    private get currentInvoice(): IStripeInvoice | undefined {
        const { invoices, selectedInvoice } = this.props;
        return selectedInvoice || invoices?.[0];
    }

    private get currentIndex(): number | undefined {
        const { invoices } = this.props;
        return invoices?.findIndex(
            ({ url }) => this.currentInvoice?.url === url,
        );
    }

    private get nextInvoice(): IStripeInvoice | undefined {
        const { invoices } = this.props;
        return (invoices || [])[(this.currentIndex || 0) - 1]; // Invoices are ordered reverse-chronologically
    }

    private get previousInvoice(): IStripeInvoice | undefined {
        const { invoices } = this.props;
        return (invoices || [])[(this.currentIndex || 0) + 1]; // Invoices are ordered reverse-chronologically
    }

    private onNext = () => {
        const { onSelectInvoice } = this.props;
        const { nextInvoice } = this;

        this.startDate = agnosticAddMonths(this.startDate, 1);
        if (nextInvoice) {
            onSelectInvoice?.(nextInvoice);
        }
    };

    private onPrevious = () => {
        const { onSelectInvoice } = this.props;
        const { previousInvoice } = this;

        this.startDate = agnosticAddMonths(this.startDate, -1);
        if (previousInvoice) {
            onSelectInvoice?.(previousInvoice);
        }
    };
}
